/* The pattern band — the third row of BOTH 07.09 mocks, which draw it identically, transcribed.
 *
 *   flex:none; display:flex; align-items:center; gap:12px; height:44px; padding:0 20px;
 *   border-bottom:1px solid var(--border)
 *
 * One stylesheet for one band. The sequence mock's pill differs from the week's by exactly three
 * declarations — height 26 instead of 28, radius 8 instead of 9, and its label at 700 instead of
 * 600 — and those three live under `[data-static]` below rather than in a second file.
 *
 * **No background of its own**, which is the one thing that tells this band apart from the two
 * above it: the top bar and the phase strip are `--bg-card` and this one shows the canvas through.
 *
 * ── Three departures were reversed on 8 September 2026, and this records what they were ──────
 * · The summary at the right edge was `--text-2`. The mock writes `--text-3`; it is `--text-3`.
 * · The pill was `--good-ink` on `--good-soft-aa` — the app's own ink over a ground stepped toward
 *   white until the pair cleared 4.5:1. The mock's own pair is `--good` on `--good-soft`, and its
 *   light values (#0c8a52 over a 14% wash) are what `--repeat-pill-*` now carry, byte for byte.
 *   They measure 3.86:1 in Flame — under AA for 11px. Recorded, flagged, not corrected: a KANON
 *   change is the designer's.
 * · The steppers rested at full strength for the same contrast reason. The mock rests them at
 *   `opacity: .85`, and that is where they rest.
 *
 * The mock builds both steppers out of `currentColor`. Stylelint's `value-keyword-case` rewrites
 * that to `currentcolor` and the value allowlist then rejects the lowercase form, so the ink travels
 * as a custom property declared once on the pill and read by both children. Same result, one green. */

.bar {
  display: flex;
  flex: none;
  gap: var(--space-3);
  align-items: center;
  height: 44px;
  padding: var(--space-0) var(--space-20px);
  border-bottom: var(--border-hairline) solid var(--border);
}

.title {
  flex: none;
  font-size: var(--fs-note);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-heading);
  color: var(--text);
  white-space: nowrap;
}

.pill {
  display: inline-flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  height: 28px;
  padding: var(--space-0) var(--space-10px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--repeat-ink);
  white-space: nowrap;
  background: var(--repeat-pill-bg);
  border-radius: var(--radius-mark);

  --repeat-ink: var(--repeat-pill-ink);
}

/* The sequence mock's pill: 26px tall at radius 8, with its one static sentence at 700. Three
 * declarations, because three is all that differs — see the header. */
.pill[data-static] {
  height: 26px;
  border-radius: var(--radius-8px);
}

.loop {
  font-size: var(--fs-label);
  line-height: var(--lh-none);
}

.repeats {
  white-space: nowrap;
}

.pill[data-static] .repeats {
  font-weight: var(--fw-bold);
}

/* 20×20 at radius 6, a hairline in the pill's own ink, resting at the mock's `.85`. A refused end
 * drops to `.3` and keeps `aria-disabled` rather than the attribute, so it stays in the tab order
 * and the reason stays reachable (view ADR §12). */
.step {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
  width: 20px;
  height: 20px;
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-none);
  color: var(--repeat-ink);
  user-select: none;
  cursor: pointer;
  background: transparent;
  opacity: 0.85;

  /* Split into three: the value allowlist requires a TOKEN colour inside a `border` shorthand. */
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: var(--repeat-ink);
  border-radius: var(--radius-xs);
}

/* The pointer target the 20px square does not carry — WCAG 2.2 SC 2.5.8, bought with a
 * pseudo-element so the band's 44px height and the pill's 28px are unchanged. */
.step::after {
  position: absolute;
  inset: var(--hit-extend);
  content: "";
}

.step:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

.step:hover:not([aria-disabled="true"]) {
  opacity: 1;
}

.step[aria-disabled="true"] {
  cursor: default;
  opacity: 0.3;
}

/* `min-width: 22px` is the mock's own, and it is what stops the pill twitching between ×9 and ×10
 * as the trainer holds the stepper. */
.count {
  min-width: 22px;
  font-size: var(--fs-label);
  font-weight: var(--fw-extrabold);
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.separator {
  opacity: 0.5;
}

.range {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* Why an end of the count cannot move. It was ORDINARY TEXT in this band until 8 September 2026;
 * the mock draws no such line, so it is now announced and drawn nowhere. **The sighted trainer who
 * never hovers has lost this explanation** — flagged for the owner rather than smuggled back. */
.reason {
  position: absolute;
  width: var(--space-1px);
  height: var(--space-1px);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.spacer {
  flex: 1;
}

.summary {
  flex: none;
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
